import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import { PersonForm } from "./PersonForm";
import { personDisplayName as fullName } from "@family/shared";
import type {
  CreateRelationshipRequest,
  Person,
  Relationship,
} from "@family/shared";

export type RelativeKind = "parent" | "spouse" | "child";

interface AddRelativeFormProps {
  familySlug: string;
  /** The person whose page we're on. */
  person: Person;
  relation: RelativeKind;
  /** Everyone in the family, for the "link existing" picker. */
  persons: Person[];
  /** Ids already related to `person` in this role (excluded from the picker). */
  excludeIds: string[];
  onDone: () => void;
  onCancel: () => void;
}

/** Build the relationship payload. For kind "parent" the API stores
 *  fromPerson = child, toPerson = parent (see relatedGroups in PersonPage). */
function relationshipFor(
  relation: RelativeKind,
  personId: string,
  otherId: string,
): CreateRelationshipRequest {
  if (relation === "child")
    return { fromPerson: otherId, toPerson: personId, kind: "parent" };
  if (relation === "parent")
    return { fromPerson: personId, toPerson: otherId, kind: "parent" };
  return { fromPerson: personId, toPerson: otherId, kind: "spouse" };
}

const RELATION_LABEL: Record<RelativeKind, string> = {
  parent: "parent",
  spouse: "spouse",
  child: "child",
};

export function AddRelativeForm({
  familySlug,
  person,
  relation,
  persons,
  excludeIds,
  onDone,
  onCancel,
}: AddRelativeFormProps) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<"new" | "existing">("new");
  const [search, setSearch] = useState("");

  const link = useMutation<Relationship, Error, string>({
    mutationFn: (otherId) =>
      apiFetch<Relationship>(`/families/${familySlug}/relationships`, {
        method: "POST",
        body: JSON.stringify(relationshipFor(relation, person.id, otherId)),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["relationships", familySlug],
      });
      void queryClient.invalidateQueries({ queryKey: ["persons", familySlug] });
      onDone();
    },
  });

  const candidates = useMemo(() => {
    const excluded = new Set([person.id, ...excludeIds]);
    const q = search.trim().toLowerCase();
    if (q.length < 2) return [];
    // Per-token match so "nunu kidane" finds Mekdelawit "Nunu" Kidane even
    // though the words aren't adjacent in the display name.
    const tokens = q.split(/\s+/).filter(Boolean);
    return persons
      .filter(
        (p) =>
          !excluded.has(p.id) &&
          tokens.every((t) => fullName(p).toLowerCase().includes(t)),
      )
      .slice(0, 8);
  }, [persons, person.id, excludeIds, search]);

  return (
    <section className="card stack" style={{ borderColor: "var(--c-blue)" }}>
      <div className="row-between">
        <h2 style={{ margin: 0, fontSize: "1.1rem" }}>
          Add {RELATION_LABEL[relation]} for {fullName(person)}
        </h2>
        <button type="button" className="btn-tertiary" onClick={onCancel}>
          Cancel
        </button>
      </div>

      <div style={{ display: "flex", gap: "var(--s-2)", flexWrap: "wrap" }}>
        <button
          type="button"
          className={mode === "new" ? undefined : "btn-secondary"}
          onClick={() => setMode("new")}
          style={{ minHeight: 34, padding: "6px 14px", fontSize: "0.9rem" }}
        >
          New person
        </button>
        <button
          type="button"
          className={mode === "existing" ? undefined : "btn-secondary"}
          onClick={() => setMode("existing")}
          style={{ minHeight: 34, padding: "6px 14px", fontSize: "0.9rem" }}
        >
          Link existing person
        </button>
      </div>

      {mode === "new" && (
        <PersonForm
          familySlug={familySlug}
          onSaved={(created) => link.mutate(created.id)}
          onCancel={onCancel}
        />
      )}

      {mode === "existing" && (
        <div className="stack">
          <div>
            <label htmlFor="link-existing-search">
              Find them in the family tree
            </label>
            <input
              id="link-existing-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Start typing a name…"
              autoFocus
            />
          </div>
          {search.trim().length >= 2 && candidates.length === 0 && (
            <p className="text-muted" style={{ margin: 0 }}>
              No match — try another spelling, or add them as a new person.
            </p>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {candidates.map((p) => (
              <button
                key={p.id}
                type="button"
                className="btn-secondary"
                onClick={() => link.mutate(p.id)}
                disabled={link.isPending}
                style={{ justifyContent: "flex-start", textAlign: "left" }}
              >
                {fullName(p)}
                {p.birthDate ? ` (${p.birthDate.slice(0, 4)})` : ""}
              </button>
            ))}
          </div>
        </div>
      )}

      {link.error && <p className="text-error">{link.error.message}</p>}
      {link.isPending && <p className="text-muted">Linking…</p>}
    </section>
  );
}
